import React, { useMemo } from 'react';

import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
} from '@mui/material';

import TableResizeHandle from './TableResizeHandle';
import groupSubtotalColumns from './groupSubtotalColumns';
import { offerTableScrollSx } from './offerTableScrollSx';
import { Model, finite, format, sum } from './presentationModel';
import useColumnOrder from './useColumnOrder';
import useScrollbarHeaderHeight from './useScrollbarHeaderHeight';
import useTableSizing from './useTableSizing';

type Offer = Model['agentHubOffers'][number];
type Column = { id: string; label: string; numeric?: boolean; value: (row: Offer) => string | number | null };
const PAGE_SIZE = 50;
const SOURCE_ORDER: Array<keyof Offer> = ['bdm_name', 'offer_country_name', 'offer_name', 'product_name', 'brand_name'];

type Props = {
  rows: Model['agentHubOffers'];
  refund: string;
  sort: string;
  direction: 'asc' | 'desc';
  page: number;
  onPage: (page: number) => void;
  onSort: (key: string) => void;
};

/** Workbook "Offers - Agent Hub" (analytics.bdm_tracker_offers_ah), led by a Grand Total across every row. */
export default function AgentHubOffers({ rows, refund, sort, direction, page, onPage, onSort }: Props) {
  const scrollRef = useScrollbarHeaderHeight();
  const { tableRef, columnWidths, narrowedColumns, resizeAxis, startResize, resizeWithKeyboard } = useTableSizing();
  const columns: Array<Column> = [
    { id: 'bdm', label: 'Bdm Name', value: (row) => row.bdm_name },
    { id: 'country', label: 'Offer Country Name', value: (row) => row.offer_country_name },
    { id: 'offerName', label: 'Offer Name', value: (row) => row.offer_name },
    { id: 'product', label: 'Product Name', value: (row) => row.product_name },
    { id: 'brand', label: 'Brand Name', value: (row) => row.brand_name },
    { id: 'ttvBr', label: 'Ttv Adjusted Br', numeric: true, value: (row) => row.ttv_adjusted_br },
    { id: 'refund', label: 'Refund', numeric: true, value: (row) => row.refund_adjusted },
    { id: 'ttvAr', label: 'Ttv Adjusted Ar', numeric: true, value: (row) => row.ttv_adjusted_ar },
    {
      id: 'gm',
      label: refund === 'Before Refund' ? 'Gm Adjusted Br' : 'Gm Adjusted Ar',
      numeric: true,
      value: (row) => row.gmAdjusted,
    },
  ];
  const { order, columnDragProps } = useColumnOrder(columns.map((column) => column.id));
  const visibleColumns = order.map((id) => columns.find((column) => column.id === id)).filter(Boolean);
  const fixedLayout = columns.every((column) => columnWidths[column.id] !== undefined);
  const sorted = useMemo(() => {
    const column = columns.find((item) => item.id === sort);
    if (!column) {
      return [...rows].sort((a, b) =>
        SOURCE_ORDER.map((field) => a[field] ?? '￿')
          .join('\u0000')
          .localeCompare(SOURCE_ORDER.map((field) => b[field] ?? '￿').join('\u0000')),
      );
    }
    return [...rows].sort((a, b) => {
      const av = column.value(a);
      const bv = column.value(b);
      if (av === null) {
        return bv === null ? 0 : 1;
      }
      if (bv === null) {
        return -1;
      }
      const value = typeof av === 'number' && typeof bv === 'number' ? av - bv : String(av).localeCompare(String(bv));
      return direction === 'asc' ? value : -value;
    });
    // Column definitions depend only on the refund basis.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, refund, sort, direction]);
  const safePage = Math.min(Math.max(0, page), Math.max(0, Math.ceil(sorted.length / PAGE_SIZE) - 1));
  return (
    <>
      <TableContainer ref={scrollRef} sx={offerTableScrollSx}>
        <Table
          ref={tableRef}
          stickyHeader
          size="small"
          sx={{
            ...(fixedLayout
              ? {
                  tableLayout: 'fixed',
                  width: columns.reduce((total, column) => total + columnWidths[column.id], 0),
                  minWidth: 0,
                }
              : { minWidth: 1800 }),
            '& .MuiTableCell-root': { py: 0.75, px: 1, fontSize: 12, lineHeight: 1.45 },
            ...(fixedLayout ? { '& .MuiTableCell-body': { overflowWrap: 'anywhere' } } : {}),
            ...(resizeAxis ? { '&, & *': { cursor: 'col-resize' } } : {}),
            '& .MuiTableCell-head': {
              height: 32,
              bgcolor: 'primary.main',
              color: '#FFFFFF',
            },
            '& .MuiTableSortLabel-root, & .MuiTableSortLabel-root.Mui-active, & .MuiTableSortLabel-icon': {
              color: '#FFFFFF !important',
            },
          }}
          aria-label="Agent Hub offer details"
        >
          {fixedLayout && (
            <Box component="colgroup">
              {visibleColumns.map((column) => (
                <Box component="col" key={column.id} sx={{ width: columnWidths[column.id] }} />
              ))}
            </Box>
          )}
          <TableHead>
            <TableRow>
              {visibleColumns.map((column) => (
                <TableCell
                  key={column.id}
                  {...columnDragProps(column.id)}
                  data-column-id={column.id}
                  align={column.numeric ? 'right' : 'left'}
                  sx={{
                    position: 'sticky',
                    overflow: 'hidden',
                    whiteSpace: narrowedColumns[column.id] ? 'normal' : 'nowrap',
                  }}
                >
                  <TableSortLabel
                    active={sort === column.id}
                    direction={sort === column.id ? direction : 'asc'}
                    onClick={() => onSort(column.id)}
                    sx={narrowedColumns[column.id] ? { maxWidth: '100%' } : undefined}
                  >
                    <Box
                      component="span"
                      sx={narrowedColumns[column.id] ? { minWidth: 0, overflowWrap: 'anywhere' } : undefined}
                    >
                      {column.label}
                    </Box>
                  </TableSortLabel>
                  <TableResizeHandle
                    axis="column"
                    label={`Resize ${column.label} column`}
                    size={columnWidths[column.id]}
                    onPointerDown={(event) => startResize(event, 'column', column.id)}
                    onKeyDown={(event) => resizeWithKeyboard(event, 'column', column.id)}
                  />
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.length > 0 && (
              <TableRow
                sx={{
                  bgcolor: (theme) => (theme.palette.mode === 'dark' ? '#0F0C08' : '#F3F1EA'),
                  '& .MuiTableCell-root': { fontWeight: 600 },
                }}
              >
                {groupSubtotalColumns(visibleColumns, (column) => Boolean(column.numeric)).map(
                  ({ measure, columns: groupColumns }, index, groups) => {
                    const column = groupColumns[0];
                    const firstDimension = !measure && !groups.slice(0, index).some((group) => !group.measure);
                    return (
                      <TableCell key={column.id} colSpan={groupColumns.length} align={measure ? 'right' : 'left'}>
                        {measure
                          ? format(
                              sum(rows, (row) => {
                                const value = column.value(row);
                                return finite(value) ? value : null;
                              }),
                              'money',
                            )
                          : firstDimension
                            ? 'Grand Total'
                            : null}
                      </TableCell>
                    );
                  },
                )}
              </TableRow>
            )}
            {sorted.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE).map((offer) => (
              <TableRow key={offer.key} hover>
                {visibleColumns.map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.numeric ? 'right' : 'left'}
                    sx={column.id === 'offerName' && !fixedLayout ? { minWidth: 220, maxWidth: 420 } : undefined}
                  >
                    {column.numeric
                      ? format(column.value(offer) as number | null, 'money')
                      : (column.value(offer) ?? 'Unknown')}
                  </TableCell>
                ))}
              </TableRow>
            ))}
            {!sorted.length && (
              <TableRow>
                <TableCell colSpan={columns.length}>No Agent Hub offer rows match the filters.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={sorted.length}
        page={safePage}
        rowsPerPage={PAGE_SIZE}
        rowsPerPageOptions={[PAGE_SIZE]}
        onPageChange={(_, next) => onPage(next)}
        sx={{
          flexShrink: 0,
          '.MuiTablePagination-toolbar': { minHeight: 32, px: 0 },
          '.MuiTablePagination-spacer': { flex: 0 },
        }}
      />
    </>
  );
}
